import { useEffect, useLayoutEffect, useRef } from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { blankSection, moveSection } from '../lib/sections'
import type { EditableSection } from '../lib/sections'

/**
 * A role's sections, edited (FR-48).
 *
 * One block per section: its heading, then its lines, one per line. Each
 * block can be moved up or down or removed, and a new one added at the end.
 * Every control is a real button whose name says which section it acts on,
 * and focus follows the section that moved, so the whole thing works from
 * the keyboard.
 */
export function SectionsEditor({
  sections,
  onChange,
}: {
  sections: EditableSection[]
  onChange: (next: EditableSection[]) => void
}) {
  // Where focus goes after the list changes: a control of a given section.
  const focusNext = useRef<string | null>(null)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = focusNext.current
    if (!id) return
    focusNext.current = null
    const el = root.current?.querySelector<HTMLElement>(`[data-focus="${id}"]`)
    // A move button that is now disabled cannot hold focus, so its section's
    // heading box takes it instead.
    if (el && !(el as HTMLButtonElement).disabled) el.focus()
    else root.current?.querySelector<HTMLElement>(`[data-focus="${id.split(':')[0]}:heading"]`)?.focus()
  })

  const set = (i: number, patch: Partial<EditableSection>) =>
    onChange(sections.map((s, j) => (j === i ? { ...s, ...patch } : s)))

  const move = (i: number, by: -1 | 1) => {
    focusNext.current = `${sections[i]!.key}:${by < 0 ? 'up' : 'down'}`
    onChange(moveSection(sections, i, by))
  }

  const remove = (i: number) => {
    const after = sections[i + 1] ?? sections[i - 1]
    focusNext.current = after ? `${after.key}:heading` : 'add'
    onChange(sections.filter((_, j) => j !== i))
  }

  const add = () => {
    const fresh = blankSection()
    focusNext.current = `${fresh.key}:heading`
    onChange([...sections, fresh])
  }

  /** How a section is named to a screen reader: its heading, or its place. */
  const nameOf = (s: EditableSection, i: number) =>
    s.heading.trim() ? `“${s.heading.trim()}”` : `section ${i + 1}`

  return (
    <div className="sections-edit" ref={root}>
      <div className="sections-edit__intro">
        <h2 className="sections-edit__title">The job description, section by section</h2>
        <p className="hint">
          Each heading as the document writes it, then its lines, one per line. Benchmarking reads
          this text again after you save.
        </p>
      </div>

      {sections.map((s, i) => {
        return (
          <fieldset className="sections-edit__block" key={s.key}>
            <legend className="sr-only">Section {i + 1} of {sections.length}</legend>
            <div className="field">
              <label htmlFor={`sec-${s.key}-heading`}>
                <span>Heading</span>
              </label>
              {/* A box that grows, because a document's heading can run to a
                  whole sentence and a one-line box cut it off. */}
              <Growing
                id={`sec-${s.key}-heading`}
                data-focus={`${s.key}:heading`}
                rows={1}
                value={s.heading}
                placeholder="No heading"
                onChange={(e) => set(i, { heading: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`sec-${s.key}-lines`}>
                <span>Lines, one per line</span>
              </label>
              <Growing
                id={`sec-${s.key}-lines`}
                rows={2}
                value={s.text}
                onChange={(e) => set(i, { text: e.target.value })}
              />
            </div>
            <div className="sections-edit__tools">
              <button
                type="button"
                className="btn btn--sm"
                data-focus={`${s.key}:up`}
                disabled={i === 0}
                aria-label={`Move ${nameOf(s, i)} up`}
                onClick={() => move(i, -1)}
              >
                ↑ Move up
              </button>
              <button
                type="button"
                className="btn btn--sm"
                data-focus={`${s.key}:down`}
                disabled={i === sections.length - 1}
                aria-label={`Move ${nameOf(s, i)} down`}
                onClick={() => move(i, 1)}
              >
                ↓ Move down
              </button>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                aria-label={`Remove ${nameOf(s, i)}`}
                onClick={() => remove(i)}
              >
                Remove
              </button>
            </div>
          </fieldset>
        )
      })}

      <div>
        <button type="button" className="btn btn--sm" data-focus="add" onClick={add}>
          + Add a section
        </button>
      </div>
    </div>
  )
}

/**
 * A text box as tall as what it holds.
 *
 * Sized by its content rather than by counting line breaks, because one line
 * of a job description is often a paragraph that wraps three times, and a box
 * sized by its breaks hid the end of it.
 */
function Growing(props: TextareaHTMLAttributes<HTMLTextAreaElement> & { 'data-focus'?: string }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const fit = () => {
      const el = ref.current
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight + 2}px`
    }
    fit()
    // A narrower window wraps the same text onto more lines.
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [props.value])
  return <textarea ref={ref} {...props} className="sections-edit__box" />
}
